/* contentLoader — single fetch helper for protected master JSON files.

   Routes data through `/api/content/{name}` which requires a session cookie
   for sensitive files. Falls back to the old `/data/{name}` path automatically
   if the API is unreachable (e.g. during static export).

   Cache strategy: keep one Promise per filename so concurrent callers share
   the same fetch. The Promise itself never throws — failures resolve to `null`
   so callers can show a clear "preview locked" state instead of a stack trace.

   Auth signal: if the backend returns 401, we resolve the promise to
   { __locked: true } so the calling page can prompt the user to sign in.
*/
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const _promises = new Map();

const _fetchOnce = async (name, { authRequired = true } = {}) => {
  try {
    const res = await axios.get(`${API}/content/${name}`);
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 && authRequired) {
      // Anonymous user → caller should render a sign-in card
      return { __locked: true, __status: 401 };
    }
    // Fall back to the legacy static path so pages still render in worst case
    try {
      const res = await fetch(`/data/${name}`);
      if (res.ok) return res.json();
    } catch (_) { /* noop */ }
    return null;
  }
};

export const loadContent = (name, opts = {}) => {
  // Don't cache locked / null responses so retrying after sign-in works.
  const cached = _promises.get(name);
  if (cached) {
    return cached.then((res) => {
      if (!res || res.__locked) {
        _promises.delete(name);
        return _fetchOnce(name, opts).then((fresh) => {
          if (fresh && !fresh.__locked) _promises.set(name, Promise.resolve(fresh));
          return fresh;
        });
      }
      return res;
    });
  }
  const p = _fetchOnce(name, opts);
  _promises.set(name, p);
  // Tear down the cache entry if it resolves to a locked/null payload so
  // subsequent reloads can re-attempt cleanly.
  p.then((res) => {
    if (!res || res.__locked) _promises.delete(name);
  });
  return p;
};

export const isLocked = (data) => Boolean(data && data.__locked);

export const invalidate = (name) => _promises.delete(name);

export default loadContent;
